import type { ReactNode } from 'react';
import React, { forwardRef, useState, useId } from 'react';
import styled, { css } from 'styled-components';

import { SubText } from './SubText';
import { ErrorMessage } from './ErrorMessage';
import { HelpText } from './HelpText';
import { ThemeTypes } from '../../../types/Theme.namespace';
import { Spacer } from '../../../helpers/genericStyles';

type CheckboxVariant = 'TICK' | 'DASH';

export interface CheckboxProps {
  /** Label for the checkbox */
  label?: ReactNode | string;
  /** Checkbox is selected. */
  checked?: boolean;
  /** Additional text to aide in use */
  helpText?: string;
  /** Disable input */
  disabled?: boolean;
  /** ID for form input */
  id?: string;
  /** Name for form input */
  name?: string;
  /** Value for form input */
  value?: string;
  /** Enable error state for input */
  hasError?: boolean;
  /** Display an error message */
  errorMessage?: string;
  /** Callback when checkbox is clicked */
  onClick?: React.MouseEventHandler<HTMLInputElement>;
  /** Callback when checkbox is toggled */
  onChange?(newChecked: boolean): void;
  /** Callback when checkbox is focussed */
  onFocus?(): void;
  /** Callback when focus is removed */
  onBlur?(): void;
  /** Checkbox style variant */
  checkboxVariant?: CheckboxVariant;
  /** Will use sub text */
  willUseSubText?: boolean;
  /** type for React Final Form */
  type?: string;
  /** Text color of label */
  labelTextColor?: ThemeTypes.Colors;
  /** If label text should be bold when checkbox is checked */
  boldWhenChecked?: boolean;
  /** class name for additional styling */
  /** For external styling */
  className?: string;
  /** Reverses the order of the label and checkbox */
  flip?: boolean;
  /** For targeting during testing */
  'data-testid'?: string;
  /** For measuring engagement */
  'data-tracking'?: string;
  /** Leading label icon  */
  leadingLabelIcon?: ReactNode;
  /** Trailing label icon */
  trailingLabelIcon?: ReactNode;
  /*
   * Touch target size
   * In time we will remove this, but we want to slowly roll out a 48px touch target
   * @deprecated
   */
  tmpIncreaseTouchTargetLarge?: boolean;
}

const Span = styled.span<{
  focused: boolean;
  disabled: boolean;
  checked?: boolean;
  checkboxVariant: CheckboxVariant;
}>`
  flex: 0 0 auto;
  border-radius: ${({ theme }) => theme.borderRadius.default};
  overflow: hidden;
  width: 24px;
  height: 24px;
  top: 0px;
  left: 0px;
  background-color: ${({ theme }) => theme.colors.WHITE};
  border: 1px solid
    ${({ theme, focused }) => (focused ? theme.colors.BLUE : theme.colors.GREY)};
  transition: all 0.2s ease-out;
  transform: rotate(0deg) scale(1);
  opacity: 1;

  &:after {
    position: absolute;
    content: '';
    left: 12px;
    top: 12px;
    height: 0px;
    width: 0px;
    border-width: 0 3px 3px 0;
    opacity: 1;
    transition: all 0.2s ease-out;
  }

  ${({ checkboxVariant }) => {
    switch (checkboxVariant) {
      case 'DASH':
        return `
        &:after {
          transform: rotate(90deg) scale(1);
          display: none;
          opacity: 1;
          left: 8px;
          top: 4px;
          width: 6px;
          height: 12px;
          border: solid #ffffff;
          border-width: 0 2px 0 0;
          background-color: transparent;
          border-radius: 0;
        }
        `;
      case 'TICK':
        return `
        &:after {
          transform: rotate(45deg) scale(1);
          display: none;
          opacity: 1;
          left: 8px;
          top: 4px;
          width: 6px;
          height: 12px;
          border: solid #ffffff;
          border-width: 0 2px 2px 0;
          background-color: transparent;
          border-radius: 0;
        }
        `;
      default:
        return `
        &:after {
          transform: rotate(45deg) scale(1);
          display: none;
          opacity: 1;
          left: 8px;
          top: 4px;
          width: 6px;
          height: 12px;
          border: solid #ffffff;
          border-width: 0 2px 2px 0;
          background-color: transparent;
          border-radius: 0;
        }     
        `;
    }
  }};

  ${({ checked }) =>
    checked &&
    css`
      background-color: ${({ theme }) => theme.colors.BLUE};
      border: 1px solid ${({ theme }) => theme.colors.WHITE};

      &:after {
        display: block;
      }
    `}

  ${({ disabled }) =>
    disabled &&
    css`
      background-color: ${({ theme }) => theme.colors.GREY_LIGHTER};
      border: 1px solid ${({ theme }) => theme.colors.GREY_LIGHT};
    `}
`;

const Label = styled.label<{
  disabled: boolean;
  hasError: boolean;
  checked?: boolean;
  textColor: ThemeTypes.Colors;
  boldWhenChecked: boolean;
  tmpIncreaseTouchTargetLarge?: boolean;
}>`
  ${({ theme }) => theme.fontSize.M16};
  display: flex;
  position: relative;
  min-height: 24px;
  cursor: pointer;
  color: ${({ theme, textColor }) => theme.colors[textColor]};
  align-items: center;

  ${({ theme, disabled }) =>
    disabled ? `color: ${theme.colors.GREY_LIGHT}; cursor: not-allowed;` : ''};
  ${({ theme, hasError }) =>
    hasError
      ? `
    color: ${theme.colors.RED_DARK};
    ${Span} {
      border: solid 1px ${theme.colors.RED_DARK}; 
    }
  `
      : ''}

  ${({ theme, checked, boldWhenChecked }) =>
    boldWhenChecked && checked && `font-weight: ${theme.fontWeight.bold};`}

  ${({ tmpIncreaseTouchTargetLarge }) =>
    tmpIncreaseTouchTargetLarge &&
    css`
      padding: 12px 0;
    `}
`;

type TInput = { checkboxVariant: CheckboxVariant };
const Input = styled.input<TInput>`
  opacity: 0;
  position: absolute;
  cursor: pointer;
`;

export const LabelText = styled.span<{
  reversed: boolean;
}>`
  flex: 1 1 auto;

  ${({ reversed, theme }) =>
    reversed
      ? css`
          order: -1;
          padding-right: ${theme.spacing.S8};
        `
      : css`
          padding-left: ${theme.spacing.S8};
        `}
`;

const LabelContent = styled.div`
  display: flex;
  align-items: center;
`;

const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>((props, ref) => {
  const {
    label = '',
    checked,
    disabled = false,
    id,
    name,
    value,
    hasError,
    helpText,
    errorMessage,
    onChange,
    onFocus,
    onBlur,
    onClick,
    checkboxVariant = 'TICK',
    willUseSubText = true,
    type,
    labelTextColor = 'BLACK',
    boldWhenChecked = false,
    className,
    flip = false,
    'data-testid': dataTestId,
    'data-tracking': dataTracking,
    leadingLabelIcon,
    trailingLabelIcon,
    tmpIncreaseTouchTargetLarge = false,
  } = props;

  const [focused, setFocused] = useState(false);

  // `id` and `name` are optional props.
  // We always want a unique `id` to link the label and input,
  // so we generate one using the `useId` hook if none is provided.

  const uniqueId = useId();
  const alwaysSetId = id || name || uniqueId;

  function handleFocus() {
    setFocused(true);
    onFocus && onFocus();
  }

  function handleBlur() {
    setFocused(false);
    onBlur && onBlur();
  }

  function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    onChange && onChange(event.currentTarget.checked);
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && !disabled) {
      event.preventDefault();
      onChange && onChange(!checked);
    }
  };

  return (
    <div className={className}>
      <LabelContent>
        <Label
          disabled={disabled}
          hasError={
            hasError || Boolean(errorMessage && errorMessage.length > 0)
          }
          htmlFor={alwaysSetId}
          textColor={labelTextColor}
          boldWhenChecked={boldWhenChecked}
          data-tracking={dataTracking}
          tmpIncreaseTouchTargetLarge={tmpIncreaseTouchTargetLarge}
        >
          <Input
            type={type ?? 'checkbox'}
            id={alwaysSetId}
            name={name}
            value={value}
            checked={checked}
            onBlur={handleBlur}
            onFocus={handleFocus}
            onChange={handleChange}
            onClick={onClick}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            checkboxVariant={checkboxVariant}
            data-testid={dataTestId}
            aria-label={name}
            ref={ref}
          />
          <Span
            focused={focused}
            checked={checked}
            disabled={disabled}
            checkboxVariant={checkboxVariant}
          />
          {leadingLabelIcon}
          <LabelText reversed={flip}>{label}</LabelText>
        </Label>
        {trailingLabelIcon}
      </LabelContent>

      {willUseSubText && (
        <>
          <Spacer marginBottom="S4" />
          <SubText>
            {errorMessage ? (
              <ErrorMessage text={errorMessage} />
            ) : (
              helpText && <HelpText text={helpText} />
            )}
          </SubText>
        </>
      )}
    </div>
  );
});

Checkbox.displayName = 'Checkbox';

export { Checkbox };
